import React, { useState, useEffect } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { FileText, Eye, Trash2, CheckCircle, XCircle, Clock, CreditCard, User, Euro } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CreditRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      const response = await fetch(`${API_URL}/api/credit-requests/`);
      const data = await response.json();
      setRequests(data);
    } catch (error) {
      console.error('Error loading credit requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (requestId, status) => {
    try {
      const response = await fetch(`${API_URL}/api/credit-requests/${requestId}/status?status=${status}`, {
        method: 'PUT',
      });
      if (response.ok) {
        toast({
          title: 'Statut mis à jour',
          description: `La demande a été marquée comme "${status}"`,
        });
        loadRequests();
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const deleteRequest = async (requestId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette demande ?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/credit-requests/${requestId}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        toast({
          title: 'Demande supprimée',
          description: 'La demande a été supprimée avec succès',
        });
        loadRequests();
      }
    } catch (error) {
      console.error('Error deleting request:', error);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Approuvée</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Rejetée</Badge>;
      default:
        return <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100">En attente</Badge>;
    }
  };

  const openDetail = (request) => {
    setSelectedRequest(request);
    setIsDetailOpen(true);
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <AdminSidebar />
      
      <div className="flex-1 p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Demandes de crédit</h1>
          <p className="text-gray-600 mt-2">Gérez les demandes de simulation reçues</p>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <FileText className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total demandes</p>
                <p className="text-2xl font-bold">{requests.length}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg bg-yellow-100">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">En attente</p>
                <p className="text-2xl font-bold">{requests.filter(r => r.status === 'pending').length}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg bg-green-100">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Approuvées</p>
                <p className="text-2xl font-bold">{requests.filter(r => r.status === 'approved').length}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg bg-red-100">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Rejetées</p>
                <p className="text-2xl font-bold">{requests.filter(r => r.status === 'rejected').length}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Table */}
        <Card className="shadow-sm">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Projet</TableHead>
                <TableHead>Montant</TableHead>
                <TableHead>Mensualité</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    Chargement...
                  </TableCell>
                </TableRow>
              ) : requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    Aucune demande de crédit pour le moment
                  </TableCell>
                </TableRow>
              ) : (
                requests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell className="text-sm">
                      {new Date(request.createdAt).toLocaleDateString('fr-FR')}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{request.firstName} {request.lastName}</p>
                        <p className="text-sm text-gray-500">{request.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>{request.projectTypeLabel}</TableCell>
                    <TableCell className="font-semibold">{request.amount?.toLocaleString()} €</TableCell>
                    <TableCell className="font-semibold text-purple-600">{request.monthlyPayment?.toFixed(2)} €</TableCell>
                    <TableCell>{getStatusBadge(request.status)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDetail(request)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-green-600 hover:text-green-700"
                          onClick={() => updateStatus(request.id, 'approved')}
                        >
                          <CheckCircle className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => updateStatus(request.id, 'rejected')}
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-gray-400 hover:text-red-600"
                          onClick={() => deleteRequest(request.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>

        {/* Detail Dialog */}
        <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Détail de la demande</DialogTitle>
            </DialogHeader>
            {selectedRequest && (
              <div className="space-y-6">
                {/* Project Info */}
                <div className="bg-purple-50 rounded-xl p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <CreditCard className="h-5 w-5 text-purple-600" />
                    <h3 className="font-bold">Projet</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Type</p>
                      <p className="font-semibold">{selectedRequest.projectTypeLabel}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Montant</p>
                      <p className="font-semibold">{selectedRequest.amount?.toLocaleString()} €</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Durée</p>
                      <p className="font-semibold">{selectedRequest.duration} mois</p>
                    </div>
                    <div>
                      <p className="text-gray-500">TAEG</p>
                      <p className="font-semibold">{selectedRequest.taeg}%</p>
                    </div>
                  </div>
                </div>

                {/* Client Info */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <User className="h-5 w-5 text-gray-600" />
                    <h3 className="font-bold">Client</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Nom complet</p>
                      <p className="font-semibold">{selectedRequest.civility === 'M' ? 'M.' : 'Mme'} {selectedRequest.firstName} {selectedRequest.lastName}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Date de naissance</p>
                      <p className="font-semibold">{new Date(selectedRequest.birthDate).toLocaleDateString('fr-FR')}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Email</p>
                      <p className="font-semibold">{selectedRequest.email}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Téléphone</p>
                      <p className="font-semibold">{selectedRequest.phone}</p>
                    </div>
                  </div>
                </div>

                {/* Payment Info */}
                <div className="bg-green-50 rounded-xl p-4 text-center">
                  <div className="flex items-center justify-center space-x-2 mb-2">
                    <Euro className="h-5 w-5 text-green-600" />
                    <h3 className="font-bold">Mensualité calculée</h3>
                  </div>
                  <p className="text-3xl font-bold text-green-600">{selectedRequest.monthlyPayment?.toFixed(2)} €/mois</p>
                  <p className="text-sm text-gray-500 mt-1">Montant total: {selectedRequest.totalAmount?.toFixed(2)} €</p>
                </div>

                {/* Actions */}
                <div className="flex space-x-3">
                  <Button
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    onClick={() => {
                      updateStatus(selectedRequest.id, 'approved');
                      setIsDetailOpen(false);
                    }}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approuver
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                    onClick={() => {
                      updateStatus(selectedRequest.id, 'rejected');
                      setIsDetailOpen(false);
                    }}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Rejeter
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default CreditRequests;
